import React, { useMemo, useState } from 'react';
import { Icon } from '../Icon/Icon';
import {
  SettingsCard,
  SettingsCards,
  SettingsFooter,
  SettingsNote,
  SettingsPageHeader,
} from '../SettingsShell/SettingsPage';
import { SetAvatar, SetChip, SetSelect, SetSwitch } from '../SettingsShell/controls';
import { SettingsRow } from '../SettingsShell/SettingsPage';
import {
  MONTH_OPTIONS,
  benefitLeadRows,
  benefitYearStore,
  daysInMonth,
} from '../../settings-benefit-year';
import { CarrierPlansModal } from './CarrierPlansModal';
import { NetworkPanelModal } from './NetworkPanelModal';
import { carrierStatus, providerNetwork, takenCarriers, totalPairs } from './insuranceDerive';
import type { CarrierStatus } from './insuranceDerive';
import type { Carrier, Provider } from '../../settings-practice/types';

export interface InsuranceViewProps {
  carriers: Carrier[];
  providers: Provider[];
  location: string;
  /** Persist the taken/paused switch on a carrier row. */
  onTogglePaused?: (carrier: Carrier, paused: boolean) => void | Promise<void>;
  /** Persist a carrier edited in the plans modal. */
  onSaveCarrier?: (next: Carrier) => void | Promise<void>;
  /** Persist one provider's in-network carriers. */
  onSaveNetwork?: (providerId: string, inNetworkCarrierIds: string[]) => void | Promise<void>;
  /** Remove a carrier; the row's remove button does nothing without it. */
  onRemoveCarrier?: (carrier: Carrier) => void | Promise<void>;
}

const STATUS_LABEL: Record<CarrierStatus, string> = {
  in_network: 'In network',
  out_of_network: 'Out of network',
  not_taken: 'Not taken',
  paused: 'Paused',
};

/** Practice management › Insurance & network (spec 15 §7). Two cards, two modals. */
export function InsuranceView({
  carriers: initial,
  providers,
  location,
  onTogglePaused,
  onSaveCarrier,
  onSaveNetwork,
  onRemoveCarrier,
}: InsuranceViewProps) {
  const [carriers, setCarriers] = useState(initial);
  const [query, setQuery] = useState('');
  const [plansFor, setPlansFor] = useState<Carrier | null>(null);
  const [networkFor, setNetworkFor] = useState<Provider | null>(null);

  const taken = takenCarriers(carriers);
  const paused = carriers.filter((c) => c.paused);

  const rows = useMemo(() => {
    const q = query.trim().toLowerCase();
    const list = [...taken, ...paused]; // paused shown below the line, not hidden
    if (!q) return list;
    return list.filter(
      (c) =>
        c.name.toLowerCase().includes(q) ||
        c.payerId.toLowerCase().includes(q) ||
        c.aliases.some((a) => a.toLowerCase().includes(q)),
    );
  }, [taken, paused, query]);

  const withSomeone = taken.filter((c) => c.inNetworkProviderIds.length > 0).length;
  const withNobody = taken.length - withSomeone;

  return (
    <div>
      <SettingsPageHeader
        eyebrow="Practice management"
        titleLead="Insurance"
        titleAccent="& network"
        sub={
          <>The carriers you take, what patients call them, and which providers are in network with each.</>
        }
      />

      <SettingsCards>
        {/* 01 — carriers */}
        <SettingsCard
          n="01"
          title="Carriers this practice takes"
          sub={
            <>Add the names patients use for each carrier.</>
          }
          headRight={
            <span className="whitespace-nowrap text-[12px] text-[var(--text-muted)]">
              <strong className="text-[var(--text-secondary)]">{taken.length}</strong> taken ·{' '}
              <strong className="text-[var(--text-secondary)]">{paused.length}</strong> paused · {carriers.length} in the
              list
            </span>
          }
        >
          <div className="px-6 pt-5">
            <span className="flex items-center gap-2 rounded-[10px] border border-[var(--border-default)] bg-[var(--surface-card)] px-3 py-2">
              <Icon kind="search" size={14} stroke={2} style={{ color: 'var(--text-muted)' }} />
              <input
                value={query}
                onChange={(e) => { setQuery(e.target.value); }}
                placeholder="Search the payer directory by name or payer ID…"
                aria-label="Search carriers"
                className="w-full bg-transparent text-[13px] text-[var(--text-primary)] outline-none placeholder:text-[var(--text-muted)]"
              />
            </span>
          </div>

          <div className="mt-4 grid grid-cols-[minmax(0,1.3fr)_minmax(0,1.2fr)_140px_92px_110px_76px] gap-x-4 px-6 pb-2 text-[10px] font-bold uppercase tracking-[0.08em] text-[var(--text-muted)]">
            <span>Carrier</span>
            <span>What patients call it</span>
            <span>Plans</span>
            <span className="text-right">In network</span>
            <span>Status</span>
            <span />
          </div>

          {rows.map((c) => {
            const status = carrierStatus(c);
            return (
              <div
                key={c.id}
                className="grid grid-cols-[minmax(0,1.3fr)_minmax(0,1.2fr)_140px_92px_110px_76px] items-center gap-x-4 border-t border-[var(--border-default)] px-6 py-3.5"
              >
                <button
                  type="button"
                  onClick={() => { setPlansFor(c); }}
                  className="flex min-w-0 items-center gap-3 text-left"
                >
                  <SetAvatar initials={c.initials} colour={c.colour} size={28} />
                  <span className="min-w-0">
                    <span className="block truncate text-[13.5px] font-semibold text-[var(--text-primary)]">
                      {c.name}
                    </span>
                    <span className="block truncate font-[var(--font-mono)] text-[11px] text-[var(--text-muted)]">
                      Payer {c.payerId}
                    </span>
                  </span>
                </button>
                <span className="flex min-w-0 flex-wrap gap-1.5">
                  {c.aliases.map((a) => (
                    <SetChip key={a}>{a}</SetChip>
                  ))}
                </span>
                <span className="flex min-w-0 flex-wrap gap-1.5">
                  {c.planTypes.map((t) => (
                    <SetChip key={t} tone="grey">
                      {t}
                    </SetChip>
                  ))}
                </span>
                <span className="text-right font-[var(--font-mono)] text-[13px]">
                  <strong
                    className={
                      c.inNetworkProviderIds.length > 0 ? 'text-[var(--text-primary)]' : 'text-[var(--text-muted)]'
                    }
                  >
                    {c.inNetworkProviderIds.length}
                  </strong>{' '}
                  <span className="text-[var(--text-muted)]">of {providers.length}</span>
                </span>
                <span>
                  <StatusPill status={status} />
                </span>
                <span className="flex items-center justify-end gap-2">
                  <SetSwitch
                    label={`${c.name} taken`}
                    checked={!c.paused}
                    onChange={(next) => {
                      setCarriers((prev) => prev.map((x) => (x.id === c.id ? { ...x, paused: !next } : x)));
                      void onTogglePaused?.(c, !next);
                    }}
                  />
                  <button
                    type="button"
                    aria-label={`Remove ${c.name}`}
                    onClick={
                      onRemoveCarrier
                        ? () => {
                            setCarriers((prev) => prev.filter((x) => x.id !== c.id));
                            void onRemoveCarrier(c);
                          }
                        : undefined
                    }
                    className="flex h-8 w-8 items-center justify-center rounded-[9px] border border-[var(--border-default)] text-[var(--text-muted)] hover:bg-[var(--surface-sunken)]"
                  >
                    <Icon kind="trash" size={13} stroke={2} />
                  </button>
                </span>
              </div>
            );
          })}

          <SettingsNote>
            {withSomeone} carriers have a provider in network. {withNobody} are accepted with nobody in network; ELVA
            tells patients they&rsquo;d pay out-of-network rates.
          </SettingsNote>
          <SettingsNote>
            Paused keeps the carrier&rsquo;s settings. While paused, ELVA says the practice doesn&rsquo;t currently take
            it.
          </SettingsNote>
        </SettingsCard>

        {/* 02 — the gaps */}
        <SettingsCard
          n="02"
          title="Who is in network with what"
          sub={
            <>What each provider can&rsquo;t take.</>
          }
        >
          <div className="mt-1 grid grid-cols-[minmax(0,1fr)_110px_minmax(0,2fr)_28px] gap-x-4 px-6 pb-2 pt-5 text-[10px] font-bold uppercase tracking-[0.08em] text-[var(--text-muted)]">
            <span>Provider</span>
            <span>In network</span>
            <span>Not in network with</span>
            <span />
          </div>
          {providers.map((p) => {
            const { inNetwork, gaps } = providerNetwork(p, carriers);
            return (
              <button
                key={p.id}
                type="button"
                onClick={() => { setNetworkFor(p); }}
                className="grid w-full grid-cols-[minmax(0,1fr)_110px_minmax(0,2fr)_28px] items-center gap-x-4 border-t border-[var(--border-default)] px-6 py-3.5 text-left hover:bg-[var(--surface-sunken)]"
              >
                <span className="flex min-w-0 items-center gap-3">
                  <SetAvatar initials={p.initials} colour={p.colour} size={28} />
                  <span className="truncate text-[13.5px] font-semibold text-[var(--text-primary)]">{p.elvaSays}</span>
                </span>
                <span className="font-[var(--font-mono)] text-[13px]">
                  <strong className="text-[var(--text-primary)]">{inNetwork.length}</strong>{' '}
                  <span className="text-[var(--text-muted)]">of {takenCarriers(carriers).length}</span>
                </span>
                <span className="flex min-w-0 flex-wrap gap-1.5">
                  {gaps.length === 0 ? (
                    <span className="text-[12px] text-[var(--text-muted)]">In network with every carrier taken.</span>
                  ) : (
                    gaps.map((g) => (
                      <SetChip key={g.id} tone="grey">
                        {g.aliases[0] ?? g.name}
                      </SetChip>
                    ))
                  )}
                </span>
                <span className="flex justify-end">
                  <Icon kind="right" size={14} stroke={2} style={{ color: 'var(--text-muted)' }} />
                </span>
              </button>
            );
          })}

        </SettingsCard>

        {/* 03 — the benefit year (amendment 20 §3.3). ONE DATE, ONE PLACE.
            Three agents each used to carry their own idea of when benefits reset
            — Recall's window, the TC's "from 1 September", and Invisalign's
            day-40 email pointing at "the window set on Recall". The lead times
            below are derived from this one date, so they cannot drift apart. */}
        <BenefitYearCard />
      </SettingsCards>

      <SettingsFooter
        location={location}
        left={
          <>
            {totalPairs(providers, carriers)} provider-and-carrier pairs in network, across {takenCarriers(carriers).length}{' '}
            carriers and {providers.length} providers.
          </>
        }
      />

      <CarrierPlansModal
        carrier={plansFor}
        providerCount={providers.length}
        onClose={() => { setPlansFor(null); }}
        onSave={(next) => {
          setCarriers((prev) => prev.map((x) => (x.id === next.id ? next : x)));
          setPlansFor(null);
          void onSaveCarrier?.(next);
        }}
      />
      <NetworkPanelModal
        provider={networkFor}
        carriers={carriers}
        onClose={() => { setNetworkFor(null); }}
        onSave={(providerId, inNetworkCarrierIds) => {
          setCarriers((prev) =>
            prev.map((c) =>
              c.paused
                ? c
                : {
                    ...c,
                    inNetworkProviderIds: inNetworkCarrierIds.includes(c.id)
                      ? Array.from(new Set([...c.inNetworkProviderIds, providerId]))
                      : c.inNetworkProviderIds.filter((id) => id !== providerId),
                  },
            ),
          );
          setNetworkFor(null);
          void onSaveNetwork?.(providerId, inNetworkCarrierIds);
        }}
      />
    </div>
  );
}

function StatusPill({ status }: { status: CarrierStatus }) {
  if (status === 'in_network') return <SetChip tone="green">{STATUS_LABEL[status]}</SetChip>;
  if (status === 'out_of_network') return <SetChip tone="amber">{STATUS_LABEL[status]}</SetChip>;
  return <SetChip tone="grey">{STATUS_LABEL[status]}</SetChip>;
}

/**
 * Insurance & network › card 03 — `Benefit year`.
 *
 * The practice chooses the date. **The lead times are fixed by ELVA in V1 and
 * shown read-only**, because how early each agent may mention unused benefits is
 * a judgement about tone, not about the practice's plan year.
 */
function BenefitYearCard() {
  const [year, setYear] = useState({ ...benefitYearStore });
  const set = (next: { month: number; day: number }) => {
    // Written to the shared store immediately, because the TC and Invisalign
    // pages read it — a local-only edit would put them back out of step.
    benefitYearStore.month = next.month;
    benefitYearStore.day = next.day;
    setYear(next);
  };
  const rows = benefitLeadRows(year);
  const maxDay = daysInMonth(year.month);

  return (
    <SettingsCard
      n="03"
      id="benefit-year"
      accent="green"
      title="Benefit year"
      sub="When patients’ insurance benefits reset. ELVA uses this date when it reminds patients about benefits they haven’t used."
    >
      <SettingsRow
        label="Benefits reset on"
        desc="Most plans reset on 31 December. Every plan is treated as resetting on this date."
        control={
          <span className="inline-flex items-center gap-2">
            <SetSelect
              ariaLabel="Benefit year reset month"
              value={String(year.month)}
              options={MONTH_OPTIONS}
              onChange={(v) => {
                const month = Number(v);
                set({ month, day: Math.min(year.day, daysInMonth(month)) });
              }}
            />
            <SetSelect
              ariaLabel="Benefit year reset day"
              value={String(year.day)}
              options={Array.from({ length: maxDay }, (_, i) => ({
                value: String(i + 1),
                label: String(i + 1),
              }))}
              onChange={(v) => { set({ ...year, day: Number(v) }); }}
            />
          </span>
        }
      />

      {/* Derived, never written. Changing the date above moves all three. */}
      <div className="bg-[#FAFBFE] px-6 py-[15px]" style={{ borderTop: '1px solid #edf0f6' }}>
        <div className="text-[10px] font-bold uppercase tracking-[0.1em] text-[var(--text-muted)]">
          When each agent may mention unused benefits
        </div>
        <div className="mt-2 flex flex-col gap-1.5">
          {rows.map((r) => (
            <div key={r.agent} className="flex items-baseline justify-between gap-4 text-[12.5px]">
              <span className="text-[var(--text-secondary)]">{r.agent}</span>
              <span className="font-[var(--font-mono)] font-semibold text-[var(--text-primary)]">{r.from}</span>
            </div>
          ))}
        </div>
      </div>

      <SettingsNote>Plans on their own benefit year aren&rsquo;t handled separately yet.</SettingsNote>
    </SettingsCard>
  );
}
